import { MutationObserverResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { noop, reduce } from 'lodash';

export type MutationStatusObserver = Omit<
    MutationObserverResult<unknown, AxiosError, unknown>,
    'mutate' | 'mutateAsync' | 'data' | 'variables' | 'context'
>;

export const combineMutationStatuses = <K extends string, M extends Record<K, MutationStatusObserver>>(
    mutations: M
): MutationStatusObserver => {
    return {
        ...reduce(
            mutations,
            (
                acc,
                {
                    error,
                    isError: curError,
                    isIdle: curIdle,
                    isPending: curPending,
                    isSuccess: curSuccess,
                    failureCount,
                    failureReason,
                    isPaused,
                    submittedAt,
                    reset,
                }
            ) => {
                const isError = acc.isError || curError;
                const isIdle = acc.isIdle && curIdle;
                const isPending = acc.isPending || curPending;
                const isSuccess = !isError && !isPending && (acc.isSuccess || curSuccess);
                return {
                    error: acc.error ?? error,
                    isError,
                    isIdle,
                    isPending,
                    isSuccess,
                    status: isError ? 'error' : isPending ? 'pending' : isSuccess ? 'success' : 'idle',
                    failureCount: acc.failureCount + failureCount,
                    failureReason: acc.failureReason ?? failureReason,
                    isPaused: acc.isPaused && isPaused,
                    submittedAt: Math.max(acc.submittedAt, submittedAt),
                    reset: () => {
                        acc.reset();
                        reset();
                    },
                };
            },
            {
                error: null,
                isError: false,
                isIdle: true,
                isPending: false,
                isSuccess: false,
                status: 'idle',
                failureCount: 0,
                failureReason: null,
                isPaused: false,
                submittedAt: 0,
                reset: noop,
            } as MutationStatusObserver
        ),
    };
};
